import { z } from 'zod/v4';
import { createDashboardDefinitionSchema, mockDataQueryDefinitionSchema } from './utils';
import {
  // Events
  timelineEventQueryDefinitionSchema,
  prometheusEventQueryDefinitionSchema,
  // Panels
  barChartDefinitionSchema,
  gaugeChartDefinitionSchema,
  heatmapDefinitionSchema,
  timeseriesChartDefinitionSchema,
  tableChartDefinitionSchema,
  markdownPanelDefinitionSchema,
  statChartDefinitionSchema,
  // DataQuery
  prometheusTimeSeriesQueryDefinitionSchema,
  changeEventsDataQueryDefinitionSchema,
  loggingQueryDefinitionSchema,
  graphiteTimeSeriesQueryDefinitionSchema,
  traceQueryDefinitionSchema,
  monitorQueryDefinitionSchema,
  // Variables
  prometheusLabelNamesVariableDefinitionSchema,
  prometheusLabelValuesVariableDefinitionSchema,
  prometheusPromQLVariableDefinitionSchema,
  staticListVariableDefinitionSchema,
  serviceTopologyPanelDefintionSchema,
  activeAlertsPanelDefinitionSchema,
  pieChartDefinitionSchema,
  logsFieldValuesVariableDefinitionSchema,
  distributionChartDefinitionSchema,
  statusHistoryPanelDefinitionSchema,
} from './plugins';

import { lintSchema } from './utils/lint-schema';

export const chronospherePanelDefinitionSchema = z.discriminatedUnion('kind', [
  activeAlertsPanelDefinitionSchema,
  barChartDefinitionSchema,
  distributionChartDefinitionSchema,
  gaugeChartDefinitionSchema,
  heatmapDefinitionSchema,
  timeseriesChartDefinitionSchema,
  tableChartDefinitionSchema,
  markdownPanelDefinitionSchema,
  serviceTopologyPanelDefintionSchema,
  statChartDefinitionSchema,
  pieChartDefinitionSchema,
  statusHistoryPanelDefinitionSchema,
]);

export const chronosphereDataQueryDefinitionSchema = z.discriminatedUnion('kind', [
  changeEventsDataQueryDefinitionSchema,
  loggingQueryDefinitionSchema,
  prometheusTimeSeriesQueryDefinitionSchema,
  graphiteTimeSeriesQueryDefinitionSchema,
  mockDataQueryDefinitionSchema,
  traceQueryDefinitionSchema,
  monitorQueryDefinitionSchema,
]);

const chronosphereEventQueryDefinitionSchema = z.discriminatedUnion('kind', [
  prometheusEventQueryDefinitionSchema,
  timelineEventQueryDefinitionSchema,
]);

const chronosphereVariableDefinitionSchema = z.discriminatedUnion('kind', [
  logsFieldValuesVariableDefinitionSchema,
  prometheusLabelNamesVariableDefinitionSchema,
  prometheusLabelValuesVariableDefinitionSchema,
  prometheusPromQLVariableDefinitionSchema,
  staticListVariableDefinitionSchema,
]);

/**
 * The Zod schema for a Chronosphere dashboard.
 */
export const chronosphereDashboardSchema = createDashboardDefinitionSchema({
  panelPluginDefinitionSchema: chronospherePanelDefinitionSchema,
  dataQueryPluginDefinitionSchema: chronosphereDataQueryDefinitionSchema,
  eventPluginDefinitionSchema: chronosphereEventQueryDefinitionSchema,
  listVariablePluginDefinitionSchema: chronosphereVariableDefinitionSchema,
});

// Lint schema in development
if (process.env.NODE_ENV !== 'production') {
  lintSchema(chronosphereDashboardSchema);
}

export interface ChronosphereDashboardSchema extends z.TypeOf<typeof chronosphereDashboardSchema> {}

export type ChronospherePanelDefinition = ChronosphereDashboardSchema['spec']['panels']['0'];

export type ChronosphereVariableDefinition = ChronosphereDashboardSchema['spec']['variables']['0'];

export type ChronospherePanelQuery = NonNullable<ChronospherePanelDefinition['spec']['queries']>[0];

export type ChronosphereDataQueryDefinition = NonNullable<
  ChronospherePanelDefinition['spec']['queries']
>[0]['spec']['plugin'];
